import { usePartThumbnailImage } from '@frontend/api';

import { PartThumbnailFallback } from './PartThumbnailFallback';
import { PartThumbnailFromBlob } from './PartThumbnailFromBlob';
import type { PartThumbnailFromBlobProps } from './PartThumbnailFromBlob';
import { PartThumbnailFromCachedBlob } from './PartThumnailFromCachedBlob';
import { ThumbnailErrorBoundary } from './ThumbnailErrorBoundary';
import { DO_NOT_CACHE_HEADERS } from './const';
import { useVisibility } from './hooks';
import { useFileType } from './hooks';

export type PartThumbnailInternalDataWrapperProps = Omit<
  PartThumbnailFromBlobProps,
  'thumbnailBlob'
> & {
  partFileUuid?: string | null;
  loadLazily?: boolean;
  shouldUseCache?: boolean;
};

/**
 * Non lazily loaded thumbnail components will enjoy a slight performance boost
 * because they do not need to manage visibility.
 * May be good to use in situations where things are virtualized already
 */
const StrictLoadedPartThumbnailDataWrapper = ({
  partFileUuid,
  shouldUseCache = false,
  ...passThroughProps
}: PartThumbnailInternalDataWrapperProps) => {
  const { fileType, filename, containsVirus, isViewerDataPurged } =
    passThroughProps;

  const { isVector } = useFileType({
    fileType,
    filename,
  });

  const partThumbnailImageQuery = usePartThumbnailImage(
    { partFileUuid },
    {
      queryOptions: {
        enabled: !containsVirus && !isViewerDataPurged,
        onError: () => console.error('Failed to load thumbnail'),
      },
      requestOptions: {
        headers: isVector ? DO_NOT_CACHE_HEADERS : undefined,
      },
    },
  );
  const { isFetching, data } = partThumbnailImageQuery;
  const thumbnailBlob = data?.thumbnailBlob;

  return shouldUseCache && partFileUuid ? (
    <PartThumbnailFromCachedBlob
      {...passThroughProps}
      partFileUuid={partFileUuid}
      isFetching={isFetching}
      thumbnailBlob={thumbnailBlob}
    />
  ) : (
    <PartThumbnailFromBlob
      {...passThroughProps}
      isFetching={isFetching}
      thumbnailBlob={thumbnailBlob}
    />
  );
};

/**
 * For lazily loaded thumbnails, we wait to fetch them until the first time
 * they become visible on the screen
 */
const LazyLoadedPartThumbnailDataWrapper = ({
  partFileUuid,
  shouldUseCache = false,
  ...passThroughProps
}: PartThumbnailInternalDataWrapperProps) => {
  // Tracks whether this thumbnail has been visible on the screen since this
  // component first mounted.
  const { visibilityRefCallback, hasBeenVisible } =
    useVisibility<HTMLDivElement>();

  const { fileType, filename, containsVirus, isViewerDataPurged } =
    passThroughProps;

  const { isVector } = useFileType({
    fileType,
    filename,
  });

  const partThumbnailImageQuery = usePartThumbnailImage(
    { partFileUuid },
    {
      queryOptions: {
        enabled: hasBeenVisible && !containsVirus && !isViewerDataPurged,
        onError: () => console.error('Failed to load thumbnail'),
      },
      requestOptions: {
        headers: isVector ? DO_NOT_CACHE_HEADERS : undefined,
      },
    },
  );
  const { isFetching, data } = partThumbnailImageQuery;
  const thumbnailBlob = data?.thumbnailBlob;

  return shouldUseCache && partFileUuid ? (
    <PartThumbnailFromCachedBlob
      {...passThroughProps}
      partFileUuid={partFileUuid}
      isFetching={isFetching}
      thumbnailBlob={thumbnailBlob}
      containerRef={visibilityRefCallback}
    />
  ) : (
    <PartThumbnailFromBlob
      {...passThroughProps}
      isFetching={isFetching}
      thumbnailBlob={thumbnailBlob}
      containerRef={visibilityRefCallback}
    />
  );
};

export type PartThumbnailDataWrapperProps =
  PartThumbnailInternalDataWrapperProps & {
    loadLazily?: boolean;
  };

export const PartThumbnailDataWrapper = ({
  loadLazily = true,
  ...passThroughProps
}: PartThumbnailDataWrapperProps) => {
  const { fileType, filename } = passThroughProps;

  const { isPdf } = useFileType({ fileType, filename });

  return (
    <ThumbnailErrorBoundary fallback={<PartThumbnailFallback isPdf={isPdf} />}>
      {loadLazily ? (
        <LazyLoadedPartThumbnailDataWrapper {...passThroughProps} />
      ) : (
        <StrictLoadedPartThumbnailDataWrapper {...passThroughProps} />
      )}
    </ThumbnailErrorBoundary>
  );
};
